import React, { useMemo, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { CircularProgress, Tooltip } from "@mui/material";

import ClipLoader from "react-spinners/ClipLoader";

import { COLORS } from "../../CreationModal.constants";
import { PillButton } from "../../CreationModal.styled";
import TypeEmblem from "../TypeEmblem";
import {
    Card,
    Controls,
    EmptyState,
    Grid,
    LoaderWrap,
    Pills,
    PillsArrow,
    PillsScroller,
    Radio,
    SearchInput,
    Tag,
    UnavailableTag,
} from "./TypeCatalog.styled";
import useScrollArrows from "./useScrollArrows";

const ALL_CATEGORY = "";
const OTHER_CATEGORY = "Other";

const normalize = (value) => (value ?? "").toString().trim().toLowerCase();

/**
 * Filters catalog items by search query and category.
 * @param {Array<{ code: string, name: string, description?: string, category?: string }>} items
 */
export const filterCatalogItems = (items, query, category) => {
    const needle = normalize(query);
    return items.filter((item) => {
        if (category && item.category !== category) return false;
        if (!needle) return true;
        return (
            normalize(item.name).includes(needle) ||
            normalize(item.description).includes(needle) ||
            normalize(item.category).includes(needle) ||
            normalize(item.code).includes(needle)
        );
    });
};

/** Local state (query + category) shared by `CatalogControls` and `CatalogGrid`. */
export const useTypeCatalog = (items) => {
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState(ALL_CATEGORY);

    const categories = useMemo(() => {
        const seen = new Map();
        items.forEach((item) => {
            const key = item.category ?? "";
            if (!key) return;
            seen.set(key, (seen.get(key) ?? 0) + 1);
        });
        const categories = Array.from(seen, ([label, count]) => ({
            label,
            count,
        }));
        const otherIndex = categories.findIndex(
            (item) => item.label === OTHER_CATEGORY,
        );
        if (otherIndex === -1) return categories;
        const [other] = categories.splice(otherIndex, 1);
        categories.push(other);
        return categories;
    }, [items]);

    const visibleItems = useMemo(
        () => filterCatalogItems(items, query, category),
        [items, query, category],
    );

    return {
        query,
        setQuery,
        category,
        setCategory,
        categories,
        visibleItems,
        totalCount: items.length,
    };
};

/** Search input + category pills. Render inside the modal header. */
export const CatalogControls = ({
    catalog,
    searchPlaceholder,
    allLabel = "All",
}) => {
    const { query, setQuery, category, setCategory, categories, totalCount } =
        catalog;
    const pillsScroll = useScrollArrows();

    return (
        <Controls>
            <SearchInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                startAdornment={<SearchIcon />}
                inputProps={{ "aria-label": searchPlaceholder }}
                autoFocus
            />
            <PillsScroller>
                <PillsArrow
                    aria-label="Scroll categories left"
                    $hidden={!pillsScroll.hasOverflow}
                    disabled={!pillsScroll.canScrollLeft}
                    onClick={() => pillsScroll.scrollBy(-1)}
                    disableRipple
                >
                    <ChevronLeftIcon />
                </PillsArrow>
                <Pills
                    ref={pillsScroll.ref}
                    role="tablist"
                    aria-label="Categories"
                >
                    <PillButton
                        role="tab"
                        aria-selected={category === ALL_CATEGORY}
                        $active={category === ALL_CATEGORY}
                        onClick={() => setCategory(ALL_CATEGORY)}
                        disableRipple
                    >
                        <span>{allLabel}</span>
                        <span className="pill-count">({totalCount})</span>
                    </PillButton>
                    {categories.map(({ label, count }) => (
                        <PillButton
                            key={label}
                            role="tab"
                            aria-selected={category === label}
                            $active={category === label}
                            onClick={() => setCategory(label)}
                            disableRipple
                        >
                            <span>{label}</span>
                            <span className="pill-count">({count})</span>
                        </PillButton>
                    ))}
                </Pills>
                <PillsArrow
                    aria-label="Scroll categories right"
                    $hidden={!pillsScroll.hasOverflow}
                    disabled={!pillsScroll.canScrollRight}
                    onClick={() => pillsScroll.scrollBy(1)}
                    disableRipple
                >
                    <ChevronRightIcon />
                </PillsArrow>
            </PillsScroller>
        </Controls>
    );
};

const CatalogCard = ({ item, selected, onSelect, onConfirm }) => {
    const { disabled, disabledTooltip } = item;

    const select = () => !disabled && onSelect(item.code);
    const confirm = () => !disabled && onConfirm?.(item.code);

    const card = (
        <Card
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled || undefined}
            $selected={selected}
            $disabled={disabled}
            disableRipple
            onClick={select}
            onDoubleClick={confirm}
            onKeyDown={(event) => {
                if (event.key === "Enter" && selected) {
                    event.preventDefault();
                    confirm();
                }
            }}
            data-cy={item.testId}
        >
            <div>
                <div className="card-head">
                    <TypeEmblem image={item.image} name={item.name} />
                    <Radio $checked={selected}>
                        {selected && <CheckIcon />}
                    </Radio>
                </div>
                <h3 className="card-title">{item.name}</h3>
                {item.description && (
                    <p className="card-description">{item.description}</p>
                )}
            </div>
            <div className="card-foot">
                {item.category && <Tag>{item.category}</Tag>}
                {disabled && item.disabledLabel && (
                    <UnavailableTag>{item.disabledLabel}</UnavailableTag>
                )}
            </div>
        </Card>
    );

    return disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip} placement="top" arrow describeChild>
            {card}
        </Tooltip>
    ) : (
        card
    );
};

/** Responsive card grid. Render inside the modal content. */
export const CatalogGrid = ({
    catalog,
    value,
    onChange,
    onConfirm,
    isLoading = false,
    emptyTitle = "Nothing matches your search.",
    emptyDescription,
}) => {
    const { visibleItems } = catalog;

    if (isLoading) {
        return (
            <LoaderWrap>
                <CircularProgress size={24} />
            </LoaderWrap>
        );
    }

    if (!visibleItems.length) {
        return (
            <EmptyState>
                <strong>{emptyTitle}</strong>
                {emptyDescription && <span>{emptyDescription}</span>}
            </EmptyState>
        );
    }

    return (
        <Grid role="radiogroup">
            {visibleItems.map((item) => (
                <CatalogCard
                    key={item.code}
                    item={item}
                    selected={item.code === value}
                    onSelect={onChange}
                    onConfirm={onConfirm}
                />
            ))}
        </Grid>
    );
};
